import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Truck, Loader2, Check, Star, FileText, Clock, DollarSign, Building2 } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const OwnerOpSignupPage = () => {
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // If already authenticated and is owner op, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.is_owner_operator) {
        navigate('/owner-operator');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Check for OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      // OAuth callback - let AuthCallback handle it
      return;
    }
  }, [location]);

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS
    const redirectUrl = window.location.origin + '/owner-op-signup/callback';
    // Store that this is an owner op signup
    localStorage.setItem('ownerOpSignup', 'true');
    localStorage.setItem('lastAuthAttempt', Date.now().toString());
    // Redirect to Emergent Auth
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Link to="/" className="flex items-center gap-3 w-fit">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl text-white">Drayly</span>
        </Link>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <Badge className="bg-orange-600/20 text-orange-400 mb-4">
              For Independent Owner Operators
            </Badge>
            <h1 className="text-4xl font-bold text-white mb-4">
              Take Control of Your Trucking Business
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Track expenses, scan receipts, log hours, and manage your documents - all in one place.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Features */}
            <div className="space-y-4">
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="h-6 w-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">AI Document Scanning</h3>
                      <p className="text-slate-400 text-sm">
                        Scan fuel receipts, scale tickets, hotel bills, and more. Our AI extracts all the data automatically.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Time Tracking</h3>
                      <p className="text-slate-400 text-sm">
                        Start/Stop your work day with one tap. Track hours automatically and get weekly summaries.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <DollarSign className="h-6 w-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Weekly Expense Reports</h3>
                      <p className="text-slate-400 text-sm">
                        Automatic weekly summaries of fuel, food, hotel, and other expenses. Know exactly where your money goes.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-6 w-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Multi-Company Support</h3>
                      <p className="text-slate-400 text-sm">
                        Work with multiple companies? Link them all and see messages, worksheets, and pay info in one dashboard.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Signup Card */}
            <div>
              <Card className="bg-slate-900 border-slate-800 sticky top-8">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Truck className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-white">Get Started Free</CardTitle>
                  <CardDescription className="text-slate-400">
                    Start with 10 free document scans per month
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Free tier features */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Check className="h-5 w-5 text-green-400" />
                      <span>10 document scans/month</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Check className="h-5 w-5 text-green-400" />
                      <span>Unlimited time tracking</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Check className="h-5 w-5 text-green-400" />
                      <span>Weekly expense summaries</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Check className="h-5 w-5 text-green-400" />
                      <span>Link to one company</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleGoogleSignup}
                    disabled={loading}
                    className="w-full h-12 bg-white hover:bg-slate-100 text-slate-900 font-medium"
                    data-testid="google-signup-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    )}
                    Continue with Google
                  </Button>

                  <p className="text-center text-xs text-slate-500">
                    By signing up, you agree to our Terms of Service and Privacy Policy
                  </p>

                  {/* Upgrade info */}
                  <div className="pt-4 border-t border-slate-800">
                    <p className="text-center text-sm text-slate-400 mb-3">
                      Need more scans?
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-center">
                      <div className="p-3 bg-slate-800/50 rounded-lg">
                        <p className="text-white font-semibold">Basic</p>
                        <p className="text-blue-400 text-lg font-bold">$14.99</p>
                        <p className="text-slate-500 text-xs">100 scans/mo</p>
                      </div>
                      <div className="p-3 bg-slate-800/50 rounded-lg relative">
                        <Star className="h-4 w-4 text-amber-400 absolute top-2 right-2" />
                        <p className="text-white font-semibold">Pro</p>
                        <p className="text-purple-400 text-lg font-bold">$29.99</p>
                        <p className="text-slate-500 text-xs">Unlimited</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <p className="text-center text-sm text-slate-500 mt-4">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-400 hover:text-blue-300">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Callback component to handle OAuth response
export const OwnerOpAuthCallback = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [processing, setProcessing] = useState(true);
  const processedRef = React.useRef(false);

  useEffect(() => {
    const processAuth = async () => {
      // Prevent double processing
      if (processedRef.current) return;
      processedRef.current = true;

      const hash = window.location.hash;
      if (!hash || !hash.includes('session_id=')) {
        toast.error('Authentication failed - no session');
        navigate('/owner-op-signup');
        return;
      }

      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (!sessionId) {
        toast.error('Invalid session');
        navigate('/owner-op-signup');
        return;
      }

      try {
        // Exchange session_id for user data
        const response = await fetch('https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data', {
          headers: { 'X-Session-ID': sessionId }
        });

        if (!response.ok) {
          throw new Error('Failed to get session data');
        }

        const userData = await response.json();

        // Create/login standalone owner operator
        const createResponse = await api.post('/auth/owner-op-google', {
          email: userData.email,
          name: userData.name,
          picture: userData.picture,
          google_id: userData.id,
          session_token: userData.session_token
        });

        if (createResponse.data.access_token) {
          // Store token
          localStorage.setItem('token', createResponse.data.access_token);
          toast.success('Welcome to Drayly!');
          
          // Clear owner op signup flag
          localStorage.removeItem('ownerOpSignup');
          
          // Redirect to owner operator dashboard
          navigate('/owner-operator', { replace: true });
        }
      } catch (error) {
        console.error('Auth error:', error);
        toast.error(error.response?.data?.detail || 'Authentication failed');
        navigate('/owner-op-signup');
      } finally {
        setProcessing(false);
      }
    };

    processAuth();
  }, [navigate, api]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
        <p className="text-white text-lg">Setting up your account...</p>
        <p className="text-slate-500 text-sm mt-2">This will only take a moment</p>
      </div>
    </div>
  );
};

export default OwnerOpSignupPage;
